/**
 * The filter in the URL (`f`, design §5.5, ED-36): base64url without padding of one canonical
 * JSON form, so an unchanged filter always gives the same `f` and "Weergave gewijzigd" can
 * compare strings. Anything that is not a version-2 filter decodes to null.
 */
import type { Condition, Filter } from './types'

export const MAX_F_LENGTH = 4096
export const EMPTY_FILTER: Filter = { version: 2, quick: [], groups: [] }

function cleanCondition(condition: Condition): Condition {
  const clean: Condition = { property: condition.property, operator: condition.operator }
  if (condition.value !== undefined && condition.value !== null) clean.value = condition.value
  return clean
}

export function normalizeFilter(filter: Partial<Filter> | null | undefined): Filter {
  return {
    version: 2,
    quick: (filter?.quick ?? []).map(cleanCondition),
    groups: (filter?.groups ?? []).filter((group) => group.length > 0).map((group) => group.map(cleanCondition)),
  }
}

export function canonicalJson(filter: Filter): string {
  const normal = normalizeFilter(filter)
  const out: Record<string, unknown> = { version: 2 }
  if (normal.quick.length > 0) out.quick = normal.quick
  if (normal.groups.length > 0) out.groups = normal.groups
  return JSON.stringify(out)
}

export function sameFilter(a: Filter, b: Filter): boolean {
  return canonicalJson(a) === canonicalJson(b)
}

export function encodeFilter(filter: Filter): string {
  let binary = ''
  for (const byte of new TextEncoder().encode(canonicalJson(filter))) binary += String.fromCharCode(byte)
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

/**
 * Whether the link of this filter can be read back: `decodeFilter` takes no `f` over
 * `MAX_F_LENGTH` characters, so a longer one is the app's own link read as broken. The limits of
 * the filter model (10 groups of 20 conditions with 100 values) allow far more than that, so
 * whoever writes an `f` checks this first and refuses with a message instead of writing it.
 */
export function fitsInUrl(filter: Filter): boolean {
  return encodeFilter(filter).length <= MAX_F_LENGTH
}

function isCondition(value: unknown): value is Condition {
  if (!value || typeof value !== 'object') return false
  const record = value as Record<string, unknown>
  return typeof record.property === 'string' && typeof record.operator === 'string'
}

export function decodeFilter(param: string): Filter | null {
  if (!param || param.length > MAX_F_LENGTH || !/^[A-Za-z0-9_-]+$/.test(param)) return null
  try {
    const base64 = param.replace(/-/g, '+').replace(/_/g, '/')
    const binary = atob(base64 + '='.repeat((4 - (base64.length % 4)) % 4))
    const text = new TextDecoder('utf-8', { fatal: true }).decode(Uint8Array.from(binary, (char) => char.charCodeAt(0)))
    const parsed: unknown = JSON.parse(text)
    if (!parsed || typeof parsed !== 'object' || (parsed as { version?: unknown }).version !== 2) return null
    const record = parsed as { quick?: unknown; groups?: unknown }
    const quick = Array.isArray(record.quick) ? record.quick.filter(isCondition) : []
    const groups = Array.isArray(record.groups)
      ? record.groups.filter(Array.isArray).map((group: unknown[]) => group.filter(isCondition))
      : []
    return normalizeFilter({ quick, groups })
  } catch {
    return null // not base64, not UTF-8 or not JSON: the link is broken, the list still opens
  }
}
